import React, { Component } from "react";
import { FormattedMessage, injectIntl, type IntlShape } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import { NavLink, Redirect, type RouteComponentProps } from "react-router-dom";

import { tryToApprovePlan } from "~/actions/approve_plan";
import { getPatientId } from "~/actions/get_patient_id";
import { isPlanApprovalStatus } from "~/common/courses";
import { remoteLog } from "~/common/logging";
import { isPatient } from "~/common/patient";
import { isUser } from "~/common/user";
import { formatPersonName } from "~/components/common/PersonName";
import { Button } from "~/components/ui/button";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { LoadingButton } from "~/components/ui/loading-button";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { buildPlanApproveDisclaimer, PLAN_APPROVE_NEED_TO_READ } from "~/features/plan-approve/disclaimer";
import { setDocumentTitle } from "~/hooks/use-document-title";
import { selectUser } from "~/slices/user";
import type { RootState } from "~/store";

const mapStateToProps = (state: RootState) => {
  return {
    user: selectUser(state),
    patient: state.patient,
  };
};

const mapDispatchToProps = {
  getPatient: getPatientId,
  planApprove: tryToApprovePlan,
};

type PlanApproveProps = PropsFromRedux & { intl: IntlShape } & RouteComponentProps<{
    patient_id: string;
    case_id: string;
  }>;

type PlanApproveState = {
  case_id: string | null;
  patient_id: string | null;
  isDisclamerAccepted: boolean;
  isSubmitting: boolean;
  redirect_to_rework: boolean;
  steps: number | string;
};

class PlanApprove extends Component<PlanApproveProps, PlanApproveState> {
  constructor(props: PlanApproveProps) {
    super(props);
    this.state = {
      case_id: null,
      patient_id: null,
      isDisclamerAccepted: false,
      isSubmitting: false,
      redirect_to_rework: true,
      steps: 0,
    };
    this.approvePlan = this.approvePlan.bind(this);
    this.acceptDisclamer = this.acceptDisclamer.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "3d_plan_approve_page");
  }

  componentDidMount() {
    const { case_id, patient_id } = this.props.match.params;
    const steps = this.props.location.search.replace("?steps=", "");

    this.setState({ case_id, patient_id, steps });
    void this.props.getPatient(Number(patient_id));

    setDocumentTitle(this.props.intl.formatMessage({ id: "plan.approve.header" }), patient_id);
  }

  acceptDisclamer(data: HTMLInputElement) {
    this.setState({ isDisclamerAccepted: data.checked });
  }

  async approvePlan() {
    try {
      this.setState({ isSubmitting: true, isDisclamerAccepted: false, redirect_to_rework: false });
      await this.props.planApprove(this.state.patient_id, this.state.case_id, this.state.steps);
    } finally {
      this.props.history.push("/pages/patients");
    }
  }

  render() {
    const { user, patient, intl } = this.props;

    if (!isUser(user) || !isPatient(patient)) {
      return (
        <Layout size="md-8">
          <Loader />
        </Layout>
      );
    }

    if (!isPlanApprovalStatus(patient.status)) {
      return <Redirect to="/pages/patients" />;
    }

    const userHasName = user.first_name.trim().length > 0 || user.last_name.trim().length > 0;

    if (!userHasName) {
      return <Redirect to="/pages/patients" />;
    }

    const clinicTitle = patient.clinic.title;

    if (!clinicTitle) {
      return (
        <Layout size="md-8">
          <Portlet as="main">
            <PortletTitle iconClassName="icon-book-open">
              <FormattedMessage id="plan.approve.header" />
            </PortletTitle>

            <div className="portlet-body">
              <FormattedMessage id="plan.approve.no_clinic" />

              <NavLink to={`/pages/patient/${this.state.patient_id}`}>
                <FormattedMessage id="plan.approve.redirect" />
              </NavLink>
            </div>
          </Portlet>
        </Layout>
      );
    }

    const DOCTOR = formatPersonName({ person: user, useMiddleName: true, isDoctor: true, user });

    // Legal copy lives in `~/features/plan-approve/disclaimer` so the redesign-v1 rebuild renders the
    // EXACT same words (see that module's note). Do not inline it back here.
    const DISCLAMER = buildPlanApproveDisclaimer(DOCTOR, clinicTitle);
    const NEET_TO_READ = PLAN_APPROVE_NEED_TO_READ;

    return (
      <Layout size="md-8">
        <Portlet as="main">
          <PortletTitle iconClassName="icon-book-open">
            <FormattedMessage id="plan.approve.header" />
          </PortletTitle>

          <div className="portlet-body">
            <div style={{ fontStyle: "italic" }}>{NEET_TO_READ[intl.locale]}</div>

            <div className="tw-my-4">
              <FormattedMessage id="plan.approve.body" /> {this.state.case_id}
            </div>

            <div className="form-group">
              <textarea
                className="form-control"
                rows={16}
                name="comment"
                defaultValue={DISCLAMER[intl.locale]}
                readOnly={true}
              />
            </div>

            <div className="form-group">
              <label
                id="approve-disclaimer-accept-label"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <input
                  id="approve-disclaimer-accept-value"
                  style={{ margin: 0 }}
                  type="checkbox"
                  onClick={(e) => this.acceptDisclamer(e.currentTarget)}
                  disabled={this.state.isSubmitting}
                />
                <FormattedMessage id="plan.approve.accept" />
              </label>
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-3 sm:tw-gap-2">
              <LoadingButton
                onClick={() => this.approvePlan()}
                disabled={!this.state.isDisclamerAccepted}
                isLoading={this.state.isSubmitting}
              >
                <FormattedMessage id="plan.confirm_plan.btn" />
              </LoadingButton>

              <NavLink to={`/pages/patient/${this.state.patient_id}/rework/${this.state.case_id}`}>
                <Button disabled={!this.state.redirect_to_rework}>
                  <FormattedMessage id="plan.approve.postpone" />
                </Button>
              </NavLink>
            </div>
          </div>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PlanApprove));
